import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  House, 
  HardHat, 
  ArrowRight, 
  CheckCircle,
  CalendarBlank,
  CalendarCheck,
  Microphone,
  Wrench,
  Users,
  ShieldCheck,
  Clock,
  CurrencyDollar,
  Star,
  Lightning,
  Hammer,
  ClipboardText,
  MapPin,
  Camera,
  ChatCircleDots,
  Flag,
  CreditCard,
  Eye,
  ChartBar,
  Handshake,
  BellRinging,
  Scales,
  FileText,
  Copy,
  Info,
  ShoppingCart,
  Megaphone,
  List,
  Storefront,
  CloudSun,
  X
} from '@phosphor-icons/react';
import { TelegramLogo, DeviceMobile } from '@phosphor-icons/react';
import { WeatherStrip } from '../components/WeatherPanel';

import { LOGO_URL } from '../config/branding';
import { trackVideoEvent } from '../utils/analytics';
import { useEffect, useState } from 'react';
import axios from 'axios';
import AudienceCarousel from '../components/landing/AudienceCarousel';
import RotatingTagline from '../components/landing/RotatingTagline';
import RealTestimonials from '../components/landing/RealTestimonials';
import BookingCalendarPromo from '../components/landing/BookingCalendarPromo';
import ShoppingListPromo from '../components/landing/ShoppingListPromo';
import LanguageMenu from '../components/LanguageMenu';
import NewsletterSignup from '../components/landing/NewsletterSignup';
import ContactFormSection from '../components/landing/ContactFormSection';
import AdBanner from '../components/AdBanner';

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.Telegram?.WebApp?.initData) navigate('/tg', { replace: true });
  }, [navigate]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartoonOpen, setCartoonOpen] = useState(false);
  const [marketItems, setMarketItems] = useState([]);
  const [weatherInput, setWeatherInput] = useState('');
  const [weatherCity, setWeatherCity] = useState('Denver, CO');

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/listings?limit=4&sort=newest`)
      .then((res) => setMarketItems(res.data.listings || []))
      .catch(() => {});
  }, []);

  const [referralBanner, setReferralBanner] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // Capture ?ref=CODE on landing — persist to localStorage so it survives the multi-step funnel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref) return;
    const code = ref.trim().toUpperCase();
    localStorage.setItem('pending_referral_code', code);
    // Look up the referrer's name for a friendly banner
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/lookup/${code}`)
      .then((r) => { if (r.data?.valid) setReferralBanner(r.data); })
      .catch(() => {});
  }, []);

  // Sticky-shrink the header once the user has scrolled past ~24px
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]">
      {/* White Header Strip — logo + Sign In inline, sticky-shrink on scroll */}
      <div
        className={`sticky top-0 z-40 bg-white border-b-4 border-black transition-shadow duration-200 ${
          scrolled ? 'shadow-[0_4px_0_#000]' : ''
        }`}
        data-testid="landing-header"
      >
        <div
          className={`max-w-7xl mx-auto px-4 flex items-center justify-between transition-all duration-200 ${
            scrolled ? 'h-14' : 'h-20'
          }`}
        >
          <div className="flex items-center gap-3 sm:gap-5">
            <img
              src={LOGO_URL}
              alt="ConstructHelp"
              className={`w-auto transition-all duration-200 ${scrolled ? 'h-9' : 'h-[52px]'}`}
            />
            <img
              src="/ai-badge.png"
              alt="Built with the best AI"
              title="See how AI powers ConstructHelp"
              onClick={() => navigate('/powered-by-ai')}
              className={`hidden md:block w-auto cursor-pointer hover:-translate-y-0.5 transition-transform duration-200 ${scrolled ? 'h-8' : 'h-11'}`}
              data-testid="ai-badge"
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageMenu compact />
            <button
              onClick={() => navigate('/login')}
              className={`flex items-center gap-2 bg-[#0F172A] hover:bg-black text-white text-xs sm:text-sm font-bold uppercase tracking-wider border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all ${
                scrolled ? 'px-3 py-1.5' : 'px-4 py-2'
              }`}
              data-testid="landing-signin-btn"
            >
              Sign In
              <ArrowRight size={14} weight="bold" />
            </button>
            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Open menu"
                className={`flex items-center justify-center bg-white hover:bg-orange-50 text-neutral-900 border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all ${
                  scrolled ? 'w-9 h-9' : 'w-11 h-11'
                }`}
                data-testid="landing-hamburger-btn"
              >
                {menuOpen ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-60 bg-white border-2 border-black shadow-[6px_6px_0px_#000] z-50" data-testid="landing-hamburger-menu">
                  {[
                    ['Features & Tools', '/home'],
                    ['Hire a Pro', '/hire'],
                    ['Browse Jobs', '/browse-jobs'],
                    ['Tools Marketplace', '/marketplace'],
                    ['Cost Guides', '/guides'],
                    ['Hiring Guides', '/hiring-guides'],
                    ['Pricing', '/pricing'],
                    ['How It Works', '/how-it-works'],
                    ['Advertise With Us', '/advertise'],
                    ['Contact', '/contact'],
                  ].map(([label, path]) => (
                    <button
                      key={path}
                      onClick={() => { setMenuOpen(false); navigate(path); }}
                      className="w-full text-left px-4 py-2.5 text-sm font-bold text-neutral-800 hover:bg-orange-50 hover:text-[#D95800] border-b border-neutral-100 last:border-0"
                      data-testid={`landing-menu-${path.replace(/\//g, '') || 'root'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-[#2B4C6F] border-y-4 border-[#D95800]">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <RotatingTagline />
          <div className="flex items-center justify-center gap-3 flex-wrap mt-4">
            <button
              onClick={() => navigate('/trust')}
              className="inline-flex items-center gap-2 bg-black text-white text-xs font-black uppercase tracking-widest px-4 py-2 border-2 border-white shadow-[3px_3px_0px_#000] hover:-translate-y-0.5 transition-transform"
              data-testid="hero-trust-link"
            >
              <ShieldCheck size={16} weight="fill" className="text-[#D95800]" />
              How we protect you
              <ArrowRight size={14} weight="bold" />
            </button>
            <button
              onClick={() => navigate('/home')}
              className="relative inline-flex items-center gap-2 bg-emerald-600 text-white text-xs font-black uppercase tracking-widest px-4 py-2 border-2 border-white shadow-[3px_3px_0px_#000] hover:-translate-y-0.5 transition-transform"
              data-testid="hero-voice-translator-link"
            >
              <Microphone size={16} weight="fill" className="text-white" />
              Voice Translator — 12 Languages
              <ArrowRight size={14} weight="bold" />
              <span className="absolute -top-2 -right-2 bg-white text-emerald-700 text-[8px] font-black uppercase px-1.5 py-0.5 border border-black rounded">New</span>
            </button>
          </div>
        </div>
      </div>

      {/* Referral Banner — shown when ?ref=CODE matched a real user */}
      {referralBanner && (
        <div className="bg-[#D95800] border-b-4 border-black" data-testid="referral-banner">
          <div className="max-w-7xl mx-auto px-4 py-3 text-center text-white text-sm font-bold flex items-center justify-center gap-2 flex-wrap">
            <span className="text-base">🎁</span>
            <span><strong>{referralBanner.referrer_name}</strong> invited you to ConstructHelp.</span>
            <span className="bg-white text-[#D95800] px-2 py-0.5 rounded-full text-xs font-black uppercase">
              You both get ${referralBanner.signup_reward} in credits
            </span>
          </div>
        </div>
      )}

      {/* Main Split Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Audience image carousel — homeowners / contractors / workers */}
        <div className="-mx-4 mb-6">
          <AudienceCarousel />
        </div>

        <AdBanner placement="homepage" />

        <div className="border-2 border-slate-200 dark:border-slate-700 rounded-3xl bg-white dark:bg-slate-900/40 p-6 md:p-10" data-testid="role-cards-section">
        <h2 className="text-2xl md:text-3xl font-black text-center mb-4 uppercase text-neutral-900 dark:text-white">
          How Can We Help You Today?
        </h2>
        <p className="text-center text-neutral-600 dark:text-neutral-400 mb-12 max-w-xl mx-auto">
          Select your path below to get started
        </p>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Homeowner Card */}
          <div 
            id="homeowner-card"
            onClick={() => navigate('/get-quotes')}
            className="group cursor-pointer bg-white dark:bg-[#0F172A] border-2 border-[#2563EB] rounded-2xl overflow-hidden shadow-[8px_8px_0px_#2B4C6F] hover:shadow-[12px_12px_0px_#2B4C6F] transition-all duration-300 hover:-translate-y-1"
            data-testid="homeowner-card"
          >
            <div className="bg-[#2B4C6F] p-6 border-b-4 border-black">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white border-2 border-black flex items-center justify-center">
                  <House size={40} weight="bold" className="text-[#2B4C6F]" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white uppercase">I'm a Homeowner</h3>
                  <p className="text-white/80">Looking for contractors</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-6">
                Get free quotes, schedule site visits, track your project from start to finish — all in one place.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Compare quotes side-by-side</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">On-site visits before you commit</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Milestone payments via secure escrow</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Real-time project tracking & chat</span>
                </div>
              </div>

              <button className="w-full h-14 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] group-hover:shadow-[6px_6px_0px_#000] transition-all flex items-center justify-center gap-2">
                Get Free Quotes
                <ArrowRight size={24} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); navigate('/how-it-works?role=homeowner'); }}
                className="relative w-full h-14 mt-3 bg-[#0F172A] hover:bg-black text-white font-black uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#2B4C6F] group-hover:shadow-[6px_6px_0px_#2B4C6F] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_#2B4C6F] transition-all flex items-center justify-center gap-2"
                data-testid="homeowner-how-it-works-btn"
              >
                <Info size={20} weight="bold" className="text-[#2B4C6F]" />
                How It Works
                <ArrowRight size={18} weight="bold" />
                <span className="absolute -top-2 -right-2 bg-[#2B4C6F] text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 border-2 border-black rotate-3 shadow-[2px_2px_0px_#000]">
                  Learn more
                </span>
              </button>

              <div className="flex items-center justify-center gap-4 mt-4 text-sm text-neutral-500">
                <div className="flex items-center gap-1">
                  <Clock size={16} weight="bold" />
                  <span>Takes 2 min</span>
                </div>
                <div className="flex items-center gap-1">
                  <ShieldCheck size={16} weight="bold" />
                  <span>100% Free</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contractor Card */}
          <div 
            id="contractor-card"
            onClick={() => navigate('/contractor-funnel')}
            className="group cursor-pointer bg-white dark:bg-[#0F172A] border-2 border-[#EA580C] rounded-2xl overflow-hidden shadow-[8px_8px_0px_#EA580C] hover:shadow-[12px_12px_0px_#EA580C] transition-all duration-300 hover:-translate-y-1"
            data-testid="contractor-card"
          >
            <div className="bg-[#EA580C] p-6 border-b-4 border-black">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white border-2 border-black flex items-center justify-center">
                  <HardHat size={40} weight="bold" className="text-[#EA580C]" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white uppercase">I'm a Contractor</h3>
                  <p className="text-white/80">Looking for jobs & leads</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-6">
                Grow your business with qualified leads and job opportunities in your area.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Access to verified homeowner leads</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">AI-powered pricing tools</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200 flex items-center gap-2 flex-wrap">
                    AI crew scheduling engine
                    <span className="bg-[#EA580C] text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">New</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Secure escrow payments</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Build your online reputation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Complete project and worker management tools</span>
                </div>
              </div>

              <button className="w-full h-14 bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] group-hover:shadow-[6px_6px_0px_#000] transition-all flex items-center justify-center gap-2">
                Join as Contractor
                <ArrowRight size={24} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); navigate('/home'); }}
                className="relative w-full h-14 mt-3 bg-[#0F172A] hover:bg-black text-white font-black uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#EA580C] group-hover:shadow-[6px_6px_0px_#EA580C] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_#EA580C] transition-all flex items-center justify-center gap-2"
                data-testid="contractor-more-info-btn"
              >
                <Info size={20} weight="bold" className="text-[#EA580C]" />
                More Information
                <ArrowRight size={18} weight="bold" />
                <span className="absolute -top-2 -right-2 bg-[#EA580C] text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 border-2 border-black rotate-3 shadow-[2px_2px_0px_#000]">
                  Learn more
                </span>
              </button>

              <div className="flex items-center justify-center gap-4 mt-4 text-sm text-neutral-500">
                <div className="flex items-center gap-1">
                  <Lightning size={16} weight="bold" />
                  <span>Start free</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star size={16} weight="bold" />
                  <span>Grow your business</span>
                </div>
              </div>
            </div>
          </div>

          {/* Worker Card */}
          <div 
            id="worker-card"
            onClick={() => navigate('/join-as-worker')}
            className="group cursor-pointer bg-white dark:bg-[#0F172A] border-2 border-[#10B981] rounded-2xl overflow-hidden shadow-[8px_8px_0px_#10B981] hover:shadow-[12px_12px_0px_#10B981] transition-all duration-300 hover:-translate-y-1"
            data-testid="worker-card"
          >
            <div className="bg-emerald-600 p-6 border-b-4 border-black">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white border-2 border-black flex items-center justify-center">
                  <Wrench size={40} weight="bold" className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white uppercase">I'm a Worker</h3>
                  <p className="text-white/80">Looking for work</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-6">
                Join our network of skilled tradespeople. Get matched with contractors and jobs near you.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Auto-match with open jobs</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Set your own rates & schedule</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Get paid via secure escrow</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle size={24} weight="bold" className="text-emerald-500 flex-shrink-0" />
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">Build your reputation & portfolio</span>
                </div>
              </div>

              <button className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] group-hover:shadow-[6px_6px_0px_#000] transition-all flex items-center justify-center gap-2">
                Find Work
                <ArrowRight size={24} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); navigate('/how-it-works?role=worker'); }}
                className="relative w-full h-14 mt-3 bg-[#0F172A] hover:bg-black text-white font-black uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#10B981] group-hover:shadow-[6px_6px_0px_#10B981] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[2px_2px_0px_#10B981] transition-all flex items-center justify-center gap-2"
                data-testid="worker-how-it-works-btn"
              >
                <Info size={20} weight="bold" className="text-emerald-400" />
                How It Works
                <ArrowRight size={18} weight="bold" />
                <span className="absolute -top-2 -right-2 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 border-2 border-black rotate-3 shadow-[2px_2px_0px_#000]">
                  Learn more
                </span>
              </button>

              <div className="flex items-center justify-center gap-4 mt-4 text-sm text-neutral-500">
                <div className="flex items-center gap-1">
                  <Clock size={16} weight="bold" />
                  <span>Takes 3 min</span>
                </div>
                <div className="flex items-center gap-1">
                  <ShieldCheck size={16} weight="bold" />
                  <span>100% Free</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Booking Calendar Promo */}
      <BookingCalendarPromo />

      {/* Homeowner Feature Showcase */}
      <div className="bg-[#2B4C6F] border-y-4 border-black py-16" data-testid="homeowner-features">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-sm font-bold uppercase tracking-widest text-white/60 mb-2">For Homeowners</p>
            <h2 className="text-2xl md:text-3xl font-black text-white uppercase mb-3">
              Every Tool You Need, In One Place
            </h2>
            <p className="text-white/80 max-w-xl mx-auto">
              From your first quote request to the final walkthrough — we've built the most complete homeowner experience in construction
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Feature 1: Quote Comparison */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-5 hover:bg-white/15 transition-colors group">
              <div className="w-12 h-12 bg-white border-2 border-black flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <ChartBar size={24} weight="bold" className="text-[#2B4C6F]" />
              </div>
              <h3 className="font-black text-white uppercase text-sm mb-2">Quote Comparison</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Receive multiple quotes and compare them side-by-side — price, timeline, contractor ratings, and reviews all in one view. The best price is automatically highlighted.
              </p>
            </div>

            {/* Feature 2: Site Visits */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-5 hover:bg-white/15 transition-colors group">
              <div className="w-12 h-12 bg-white border-2 border-black flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <MapPin size={24} weight="bold" className="text-[#2B4C6F]" />
              </div>
              <h3 className="font-black text-white uppercase text-sm mb-2">On-Site Inspections</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Contractors can request a site visit before quoting. They propose times, you pick one. After the visit, see their inspection photos and notes — so you know exactly what you're paying for.
              </p>
            </div>

            {/* Feature 3: Photo Evidence */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-5 hover:bg-white/15 transition-colors group">
              <div className="w-12 h-12 bg-white border-2 border-black flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <Camera size={24} weight="bold" className="text-[#2B4C6F]" />
              </div>
              <h3 className="font-black text-white uppercase text-sm mb-2">Photo-Backed Quotes</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Every site visit comes with a photo gallery. See exactly what the contractor found — water damage, structural issues, material needs — all documented with photos alongside the final quote.
              </p>
            </div>

            {/* Feature 4: Milestone Escrow */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-5 hover:bg-white/15 transition-colors group">
              <div className="w-12 h-12 bg-white border-2 border-black flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <ShieldCheck size={24} weight="bold" className="text-[#2B4C6F]" />
              </div>
              <h3 className="font-black text-white uppercase text-sm mb-2">Milestone Escrow</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Your money stays safe. Pre-fund the project budget and payments only release to the contractor as each milestone is completed. You never pay for unfinished work.
              </p>
            </div>

            {/* Feature 5: Project Tracker */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-5 hover:bg-white/15 transition-colors group">
              <div className="w-12 h-12 bg-white border-2 border-black flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <Flag size={24} weight="bold" className="text-[#2B4C6F]" />
              </div>
              <h3 className="font-black text-white uppercase text-sm mb-2">Live Project Tracking</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Watch your project come to life. A shared timeline with milestones, progress bar, photo updates from the contractor, and payment tracking — all visible in real time.
              </p>
            </div>

            {/* Feature 6: Direct Chat */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 p-5 hover:bg-white/15 transition-colors group">
              <div className="w-12 h-12 bg-white border-2 border-black flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                <ChatCircleDots size={24} weight="bold" className="text-[#2B4C6F]" />
              </div>
              <h3 className="font-black text-white uppercase text-sm mb-2">Instant Messaging</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Message any contractor directly from your dashboard. No phone tag, no email chains. Get push notifications when they respond, and keep your entire conversation history.
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/signup?type=homeowner')}
              className="h-14 px-10 bg-white text-[#2B4C6F] font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all inline-flex items-center gap-2"
              data-testid="features-cta"
            >
              <House size={22} weight="bold" />
              Start Your Project Free
              <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Escrow Trust Band — payment protection */}
      <div className="bg-[#D1FAE5] border-b-4 border-black py-14" data-testid="escrow-trust-band">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="md:w-2/5">
              <div className="inline-flex items-center gap-2 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest px-3 py-1.5 border-2 border-black shadow-[2px_2px_0px_#000] mb-4">
                <ShieldCheck size={14} weight="fill" /> Payment Protection
              </div>
              <h2 className="text-2xl md:text-3xl font-black uppercase text-neutral-900 leading-tight mb-3">
                Your money stays protected
              </h2>
              <p className="text-neutral-700">
                Never pay a contractor upfront again. Your payment sits in secure escrow and only moves when <span className="font-bold">you approve the work</span> — milestone by milestone.
              </p>
            </div>
            <div className="md:w-3/5 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { n: '1', title: 'Fund Escrow', desc: 'Your payment is held securely — not by the contractor.' },
                { n: '2', title: 'Approve Milestones', desc: 'Work gets done. You review and approve each stage.' },
                { n: '3', title: 'Funds Release', desc: 'Only approved milestones get paid out. Disputes reviewed by our team.' },
              ].map((s) => (
                <div key={s.n} className="bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#0F172A]">
                  <span className="w-8 h-8 inline-flex items-center justify-center bg-emerald-600 text-white font-black border-2 border-black mb-2">{s.n}</span>
                  <p className="font-black text-sm uppercase text-neutral-900">{s.title}</p>
                  <p className="text-xs text-neutral-600 mt-1">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/get-quotes')}
              className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all inline-flex items-center gap-2"
              data-testid="escrow-band-cta"
            >
              <ShieldCheck size={20} weight="fill" /> Start a Protected Project <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* How It Works — Full Lifecycle */}
      <div className="max-w-5xl mx-auto px-4 py-16" data-testid="how-it-works">
        <h2 className="text-2xl md:text-3xl font-black text-center mb-3 uppercase text-neutral-900 dark:text-white">
          Your Project, Step by Step
        </h2>
        <p className="text-center text-neutral-600 dark:text-neutral-400 mb-12 max-w-lg mx-auto">
          From idea to completion — here's how ConstructHelp protects you at every stage
        </p>

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#2B4C6F]/30 hidden md:block" />

          <div className="space-y-8">
            {[
              { num: '01', icon: ClipboardText, title: 'Describe Your Project', desc: 'Tell us what you need — kitchen remodel, roof repair, new deck. It takes 2 minutes.', color: 'bg-[#2B4C6F]' },
              { num: '02', icon: Users, title: 'Contractors Compete for You', desc: 'Local verified contractors see your project and send you quotes. You do nothing but wait.', color: 'bg-[#2B4C6F]' },
              { num: '03', icon: MapPin, title: 'Optional Site Visit', desc: 'Want an accurate quote? Contractors visit your property, document findings with photos, and send a detailed estimate.', color: 'bg-blue-600' },
              { num: '04', icon: ChartBar, title: 'Compare & Accept', desc: 'See all quotes side-by-side with prices, timelines, ratings, and site visit photos. Accept the best one.', color: 'bg-emerald-600' },
              { num: '05', icon: CreditCard, title: 'Fund the Escrow', desc: 'Your budget goes into secure escrow. Money only releases as milestones are completed — never upfront.', color: 'bg-emerald-600' },
              { num: '06', icon: Flag, title: 'Track Progress Live', desc: 'Watch milestones get checked off, see photo updates from the job site, and chat directly with your contractor.', color: 'bg-[#D95800]' },
              { num: '07', icon: Star, title: 'Review & Complete', desc: 'Once the project is done, leave a review to help other homeowners. Your payment is already handled.', color: 'bg-[#D95800]' },
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="flex gap-5 items-start">
                  <div className={`w-12 h-12 ${step.color} border-2 border-black flex items-center justify-center flex-shrink-0 relative z-10`}>
                    <Icon size={24} weight="bold" className="text-white" />
                  </div>
                  <div className="flex-1 bg-white dark:bg-[#0F172A] border-2 border-black p-4 shadow-[3px_3px_0px_#000]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-neutral-400 uppercase">Step {step.num}</span>
                    </div>
                    <h4 className="font-black text-neutral-900 dark:text-white uppercase text-sm">{step.title}</h4>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Promo Video Section */}
      <div className="bg-white dark:bg-neutral-900 border-y-4 border-black py-16" data-testid="promo-video-section">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#D95800]/10 border-2 border-[#D95800] px-3 py-1.5 mb-5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D95800] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D95800]"></span>
              </span>
              <span className="text-xs font-black text-[#D95800] uppercase tracking-widest">Watch the promo</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-neutral-900 dark:text-white uppercase tracking-tight mb-4">
              See ConstructHelp<br/>in action
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-6 leading-relaxed">
              Homeowners post projects. Contractors win real leads. Workers get hired.
              One platform, escrow-protected — see how it works in under a minute.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/get-quotes')}
                className="inline-flex items-center gap-2 bg-[#2B4C6F] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
                data-testid="promo-video-homeowner-cta"
              >
                Get Quotes <ArrowRight size={16} weight="bold" />
              </button>
              <button
                onClick={() => navigate('/contractor-funnel')}
                className="inline-flex items-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
                data-testid="promo-video-contractor-cta"
              >
                Join as a Pro <ArrowRight size={16} weight="bold" />
              </button>
              <button
                onClick={() => setCartoonOpen(true)}
                className="inline-flex items-center gap-2 bg-white dark:bg-neutral-800 text-[#0F172A] dark:text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#2B4C6F] hover:-translate-y-0.5 transition-transform"
                data-testid="cartoon-video-open-btn"
              >
                <span className="text-[#D95800]">▶</span> Watch the Cartoon
              </button>
            </div>
          </div>
          <div className="flex justify-center md:justify-end">
            <div className="relative w-full max-w-[320px] rounded-[2rem] border-4 border-black bg-black shadow-[8px_8px_0px_#D95800] overflow-hidden">
              <video
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_58284b88-53d8-4c2d-a757-f70994c6780a/artifacts/vwt4er10_My%20movie%2053.mp4"
                onPlay={() => trackVideoEvent('landing-promo', 'play')}
                onEnded={() => trackVideoEvent('landing-promo', 'complete')}
                controls
                playsInline
                preload="metadata"
                className="w-full aspect-[9/16] object-cover"
                data-testid="promo-video-player"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Weather Forecaster Showcase */}
      <div className="bg-[#0F172A] border-b-4 border-black py-14" data-testid="weather-showcase-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-sky-400 mb-2 flex items-center gap-1.5">
                <CloudSun size={16} weight="fill" /> New — Job Weather Forecaster
              </p>
              <h2 className="text-2xl md:text-3xl font-black uppercase text-white">
                Never Get Rained Out Again
              </h2>
              <p className="text-sm md:text-base text-neutral-400 mt-3">
                ConstructHelp checks the weather every morning for your city and every active job site. If rain is coming, your crew gets an alert before they load the truck — and rain days are flagged right on your Crew Calendar.
              </p>
              <ul className="mt-4 space-y-2">
                {[
                  'Automatic daily rain check with morning alerts',
                  'Rain warnings on the Crew Calendar & every job page',
                  '7-day job-site forecasts — free, no setup',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-neutral-300">
                    <ShieldCheck size={16} weight="fill" className="text-sky-400 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/signup')}
                className="mt-5 inline-flex items-center gap-2 bg-sky-500 text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
                data-testid="weather-showcase-cta"
              >
                Get Rain Alerts Free <ArrowRight size={16} weight="bold" />
              </button>
            </div>

            {/* Live demo */}
            <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-2xl shadow-[6px_6px_0px_#0284C7] p-5" data-testid="weather-demo-widget">
              <p className="text-[10px] font-black uppercase tracking-widest text-neutral-400 mb-2">Try it live — check any job site</p>
              <form
                onSubmit={(e) => { e.preventDefault(); setWeatherCity(weatherInput.trim()); }}
                className="flex gap-2 mb-4"
              >
                <input
                  value={weatherInput}
                  onChange={(e) => setWeatherInput(e.target.value)}
                  placeholder="City, ST — e.g. Denver, CO"
                  className="flex-1 text-sm px-3 py-2.5 border-2 border-black rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:outline-none"
                  data-testid="weather-demo-input"
                />
                <button type="submit" className="bg-[#0F172A] text-white font-black uppercase text-xs px-4 border-2 border-black rounded-lg" data-testid="weather-demo-check-btn">
                  Check
                </button>
              </form>
              <p className="text-xs font-bold text-neutral-500 mb-2">{weatherCity}</p>
              <WeatherStrip key={weatherCity} location={weatherCity} showError />
            </div>
          </div>
        </div>
      </div>

      {/* Telegram Mini App Showcase */}
      <div className="bg-[#1C93E3] border-b-4 border-black py-14" data-testid="telegram-showcase-section">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-white/90 mb-2 flex items-center gap-1.5">
              <TelegramLogo size={16} weight="fill" /> New — ConstructHelp on Telegram
            </p>
            <h2 className="text-2xl md:text-3xl font-black uppercase text-white leading-tight">
              The Whole Platform,<br />Right Inside Telegram
            </h2>
            <p className="text-sm md:text-base text-white/85 mt-3 max-w-lg">
              Post projects, find pros, chat, and shop the tools marketplace without leaving Telegram. No email, no password — tap once and you're in.
            </p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-center gap-2 text-sm font-bold text-white">
                <DeviceMobile size={18} weight="fill" /> One-tap sign in with your Telegram account
              </li>
              <li className="flex items-center gap-2 text-sm font-bold text-white">
                <BellRinging size={18} weight="fill" /> Instant alerts for jobs, offers & messages — right in your chats
              </li>
            </ul>
            <a
              href="https://t.me/Constructhelp123bot"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#1C93E3] font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform mt-6"
              data-testid="telegram-showcase-open-btn"
            >
              <TelegramLogo size={18} weight="fill" /> Open in Telegram
            </a>
          </div>
          <div className="flex justify-center md:justify-end">
            <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_#000] p-6 text-center" data-testid="telegram-qr-card">
              <img src="/telegram-qr.png" alt="Scan to open ConstructHelp on Telegram" className="w-48 h-48 mx-auto" data-testid="telegram-qr-image" />
              <p className="font-black uppercase text-sm text-neutral-900 mt-3">Scan with your phone</p>
              <p className="text-xs text-neutral-500 mt-1">@Constructhelp123bot</p>
            </div>
          </div>
        </div>
      </div>

      {/* Marketplace Showcase */}
      <div className="bg-[#FDF6EC] dark:bg-neutral-800 border-b-4 border-black py-14" data-testid="marketplace-showcase-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-[#D95800] mb-2 flex items-center gap-1.5">
                <Storefront size={16} weight="fill" /> New — Tools & Materials Marketplace
              </p>
              <h2 className="text-2xl md:text-3xl font-black uppercase text-neutral-900 dark:text-white">
                Buy & Sell Tools and Leftover Materials
              </h2>
              <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-400 mt-2 max-w-xl">
                Used drills, extra lumber, copper pipe from the last job — list it in minutes or grab a deal near you. Pay securely through the platform or arrange pickup with the seller.
              </p>
            </div>
            <button
              onClick={() => navigate('/marketplace')}
              className="inline-flex items-center gap-2 bg-[#D95800] text-white font-black uppercase text-sm px-6 py-3 border-2 border-black shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-transform"
              data-testid="marketplace-showcase-browse-btn"
            >
              Browse Marketplace <ArrowRight size={16} weight="bold" />
            </button>
          </div>

          {marketItems.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="marketplace-showcase-grid">
              {marketItems.map((item) => (
                <button
                  key={item._id}
                  onClick={() => navigate(`/marketplace/listing/${item._id}`)}
                  className="group text-left bg-white dark:bg-neutral-900 border-2 border-black rounded-xl overflow-hidden shadow-[4px_4px_0px_#000] hover:-translate-y-1 transition-transform"
                  data-testid={`showcase-item-${item._id}`}
                >
                  <div className="aspect-square bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                    {item.photos?.length > 0 ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/photo/${item.photos[0]}`}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Storefront size={40} weight="duotone" className="text-neutral-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-black text-[#D95800]">
                      {item.price === 0 ? 'Free' : `$${Number(item.price).toLocaleString()}`}
                    </p>
                    <p className="text-xs font-bold text-neutral-700 dark:text-neutral-300 truncate">{item.title}</p>
                    <p className="text-[10px] text-neutral-500 mt-0.5">{item.city}, {item.state}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <span className="inline-flex items-center gap-1.5 bg-white dark:bg-neutral-900 border-2 border-black rounded-full px-3 py-1.5 text-xs font-bold text-neutral-700 dark:text-neutral-300">
              <ShieldCheck size={14} weight="bold" className="text-emerald-600" /> Secure Stripe checkout
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white dark:bg-neutral-900 border-2 border-black rounded-full px-3 py-1.5 text-xs font-bold text-neutral-700 dark:text-neutral-300">
              <ShoppingCart size={14} weight="bold" className="text-[#2B4C6F]" /> Free to list — anyone can sell
            </span>
            <button
              onClick={() => navigate('/marketplace/new')}
              className="inline-flex items-center gap-1.5 bg-[#2B4C6F] text-white border-2 border-black rounded-full px-4 py-1.5 text-xs font-black uppercase hover:-translate-y-0.5 transition-transform"
              data-testid="marketplace-showcase-sell-btn"
            >
              Sell an Item
            </button>
          </div>
        </div>
      </div>

      {/* Value Proposition Section */}
      <div className="bg-[#0F172A] border-y-4 border-black py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase mb-6">
            Why Choose ConstructHelp?
          </h2>
          <p className="text-lg text-neutral-300 mb-8 max-w-2xl mx-auto">
            We're building the most trusted marketplace for home improvement. 
            Whether you need work done or you're looking for your next project, 
            we make the connection seamless and secure.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="bg-white/5 border border-white/10 p-5">
              <ShieldCheck size={36} weight="bold" className="text-[#2B4C6F] mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">Verified Pros</h3>
              <p className="text-xs text-neutral-400">Background-checked & reviewed contractors</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-5">
              <CurrencyDollar size={36} weight="bold" className="text-emerald-500 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">Escrow Protection</h3>
              <p className="text-xs text-neutral-400">Pay only for completed milestones</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-5">
              <Camera size={36} weight="bold" className="text-[#D95800] mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">Photo Evidence</h3>
              <p className="text-xs text-neutral-400">Site visit photos justify every quote</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-5">
              <Scales size={36} weight="bold" className="text-violet-400 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">Compare Bids</h3>
              <p className="text-xs text-neutral-400">Side-by-side comparison of worker proposals</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-5">
              <FileText size={36} weight="bold" className="text-cyan-400 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">Export Reports</h3>
              <p className="text-xs text-neutral-400">PDF reports and completion certificates</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-5">
              <Copy size={36} weight="bold" className="text-amber-400 mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">Job Templates</h3>
              <p className="text-xs text-neutral-400">Save & repost frequent jobs with one click</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-5">
              <CalendarBlank size={36} weight="bold" className="text-[#2B4C6F] mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">Book by Calendar</h3>
              <p className="text-xs text-neutral-400">Request open days & one-tap accept in chat</p>
            </div>
            <div className="relative bg-white/5 border border-[#EA580C]/50 p-5" data-testid="landing-ai-scheduler-card">
              <span className="absolute top-2 right-2 bg-[#EA580C] text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">New</span>
              <CalendarCheck size={36} weight="bold" className="text-[#EA580C] mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">AI Crew Scheduling</h3>
              <p className="text-xs text-neutral-400">Describe the job — AI builds the phase plan & books the right crew</p>
            </div>
            <div className="relative bg-white/5 border border-[#10B981]/50 p-5" data-testid="landing-shopping-lists-card">
              <span className="absolute top-2 right-2 bg-[#10B981] text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full">New</span>
              <ShoppingCart size={36} weight="bold" className="text-[#34D399] mx-auto mb-3" />
              <h3 className="font-bold text-white mb-1 text-sm">AI Shopping Lists</h3>
              <p className="text-xs text-neutral-400">AI drafts the materials list — workers check off, log prices & snap the receipt</p>
            </div>
          </div>
        </div>
      </div>

      {/* Smart Shopping Lists Promo */}
      <ShoppingListPromo role="contractor" />

      {/* Real Testimonials */}
      <RealTestimonials />

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-[#2B4C6F] to-[#D95800] border-t-4 border-black py-12">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/90 mb-8">
            Join thousands of homeowners who've found their perfect contractor through ConstructHelp
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/get-quotes')}
              className="h-14 px-8 bg-white text-[#2B4C6F] font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all flex items-center justify-center gap-2"
              data-testid="final-cta-homeowner"
            >
              <House size={24} weight="bold" />
              Get Free Quotes
            </button>
            <button
              onClick={() => navigate('/contractor-funnel')}
              className="h-14 px-8 bg-[#0F172A] text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all flex items-center justify-center gap-2"
              data-testid="final-cta-contractor"
            >
              <HardHat size={24} weight="bold" />
              Join as Contractor
            </button>
            <button
              onClick={() => navigate('/join-as-worker')}
              className="h-14 px-8 bg-[#D95800] text-white font-bold uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all flex items-center justify-center gap-2"
              data-testid="final-cta-worker"
            >
              <Wrench size={24} weight="bold" />
              Find Work
            </button>
          </div>
          {/* Secondary Sign In link */}
          <p className="text-center text-neutral-400 text-sm mt-8">
            Already a member?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-white font-bold underline underline-offset-4 hover:text-[#D95800] transition-colors"
              data-testid="landing-signin-link-bottom"
            >
              Sign in here
            </button>
          </p>
        </div>
      </div>

      {/* Newsletter signup */}
      <NewsletterSignup />

      {/* Contact form */}
      <ContactFormSection />

      {/* Advertise promo */}
      <div className="bg-neutral-900 border-t-4 border-[#D95800] py-14" data-testid="advertise-promo-section">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 bg-[#D95800] border-2 border-white flex items-center justify-center shrink-0">
                <Megaphone size={24} weight="fill" className="text-white" />
              </div>
              <p className="text-xs font-black uppercase tracking-wider text-[#D95800]">For suppliers, tool brands & local businesses</p>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-white uppercase mb-3">Put your brand in front of people building right now</h2>
            <p className="text-neutral-400 max-w-2xl">
              Homeowners planning projects and pros buying materials browse ConstructHelp every day.
              Book a banner spot from <span className="text-white font-bold">$39/week</span> — including
              exclusive trade slots where you're the only advertiser in your category.
            </p>
          </div>
          <div className="shrink-0 flex flex-col items-start md:items-end gap-3">
            <button
              onClick={() => navigate('/advertise')}
              className="h-14 px-8 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] transition-all flex items-center gap-2"
              data-testid="advertise-promo-cta"
            >
              Advertise With Us
              <ArrowRight size={20} weight="bold" />
            </button>
            <button
              onClick={() => navigate('/advertising')}
              className="text-neutral-400 text-sm font-bold underline underline-offset-4 hover:text-white transition-colors"
              data-testid="advertise-promo-learn-more"
            >
              See placements & pricing
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#0F172A] py-6 border-t-2 border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 text-center text-neutral-500 text-sm">
          <p>&copy; 2026 ConstructHelp. All rights reserved.</p>
        </div>
      </div>

      {/* Cartoon Promo Video Popup */}
      {cartoonOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setCartoonOpen(false)}
          data-testid="cartoon-video-modal"
        >
          <div
            className="relative w-full max-w-[420px] bg-black border-4 border-black rounded-[2rem] shadow-[8px_8px_0px_#D95800] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setCartoonOpen(false)}
              className="absolute top-3 right-3 z-10 bg-white text-black border-2 border-black rounded-full p-1.5 hover:bg-[#D95800] hover:text-white transition-colors"
              data-testid="cartoon-video-close-btn"
              aria-label="Close video"
            >
              <X size={18} weight="bold" />
            </button>
            <video
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_58284b88-53d8-4c2d-a757-f70994c6780a/artifacts/2isc7s9h_My%20movie%2061.mp4"
              poster="/cartoon-poster.jpg"
              onPlay={() => trackVideoEvent('landing-cartoon', 'play')}
              onEnded={() => trackVideoEvent('landing-cartoon', 'complete')}
              controls
              playsInline
              className="w-full max-h-[70vh] object-contain"
              data-testid="cartoon-video-player"
            />
            <div className="flex gap-2 p-3 bg-[#0F172A] border-t-2 border-neutral-800">
              <button
                onClick={() => { setCartoonOpen(false); navigate('/get-quotes'); }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#2B4C6F] text-white font-black uppercase text-xs px-3 py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] hover:-translate-y-0.5 transition-transform"
                data-testid="cartoon-cta-homeowner"
              >
                Get Quotes <ArrowRight size={14} weight="bold" />
              </button>
              <button
                onClick={() => { setCartoonOpen(false); navigate('/contractor-funnel'); }}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#D95800] text-white font-black uppercase text-xs px-3 py-2.5 border-2 border-black shadow-[3px_3px_0px_#000] hover:-translate-y-0.5 transition-transform"
                data-testid="cartoon-cta-contractor"
              >
                Join as a Pro <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
